<template>
  <div class="form-section">
    <SectionSeparatorWrapper :separator="false" headerText="My details" headerRequiredFieldsText="indicates required field">
      <FormField :labelText="refData.startDate.label" required>
        <p class="disabled-input half-width" :aria-label="refData.startDate.label">{{ reverseFormatFormDate(step1.startDate) }}</p>
      </FormField>
      <FormField :labelText="refData.state.label" :helperText="refData.state.helpText" required>
        <DropdownSelect :items="stateList" v-model="v$.step1.state.$model" :initialSelect="step1.state" />
      </FormField>
      <FormField :labelText="refData.eligibility.label" :helperText="refData.eligibility.helpText" required>
        <DropdownSelect :items="eligibilityOptionsList" v-model="v$.step1.eligibility.$model" :initialSelect="eligibilityInitialSelect" />
      </FormField>
      <FormField v-if="recentDischargeShow" :labelText="refData.discharge.label" :helperText="refData.discharge.helpText" required>
        <ul class="radio-group">
          <li v-for="option in [true, false]" :key="`recentDischarge-${option}`">
            <input type="radio" :id="`recentDischarge-${option}`" :value="option" name="recentDischarge" v-model="step1.recentDischarge" />
            <label :for="`recentDischarge-${option}`">{{ option === true ? 'Yes' : 'No' }}</label>
          </li>
        </ul>
      </FormField>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { EligibilityTypes } from '@/enums';
import { FormField, SectionSeparatorWrapper } from '@/components';
import { getCurrentDate, getPastOrLaterDateFromInitialDate, reverseFormatFormDate } from '@general/js/utils';
import { isDateInRange } from '@/utils';
import { DropdownSelect } from '@general/js/components';
import { findPolicyHolder } from '@/utils/ChangeProduct/ExtraUtils';
import { mapEligibilityNames } from '@/utils/ChangeCoverUtils';
import ChangeCoverMixins from '../ChangeCoverMixins';

export default {
  name: 'MyDetails',
  setup() {
    return { v$: useVuelidate() };
  },

  mixins: [ChangeCoverMixins],

  components: {
    DropdownSelect,
    FormField,
    SectionSeparatorWrapper,
  },

  props: {
    // UI data for change cover
    refData: {
      type: Object,
      required: true,
    },
    prefillDetails: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      step1: {
        startDate: getCurrentDate(),
        state: null,
        eligibility: null,
        recentDischarge: null,
      },
      isActive: false,
    };
  },

  beforeMount() {
    if (this.prefillDetails && this.$route.params?.fromCoverSelectionPage !== true) {
      this.getStateEligibilityDetails();
      this.step1 = { ...this.myDetails, ...this.step1 };
      this.updateMyDetails({ ...cloneDeep(this.step1) });
    }
  },

  activated() {
    this.isActive = true;
    this.updateMyDetails({ ...this.myDetails, startDate: getCurrentDate() });
    this.step1 = this.myDetails;
  },

  deactivated() {
    this.isActive = false;
  },

  watch: {
    v$: {
      handler(newData) {
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('responseUpdated', {
              response: this.step1,
              isInvalid: newData.$invalid || newData.$error,
            });
          });
        }
      },
      deep: true,
    },
  },

  computed: {
    isVeteranCardShow() {
      if (EligibilityTypes.SERVING.includes(this.step1.eligibility) || EligibilityTypes.EX_SERVING.includes(this.step1.eligibility)) {
        return true;
      }
      return false;
    },
    eligibilityOptionsList() {
      return this.refData.eligibility.options.map((option) => {
        return { name: option.display, value: option.returnValue };
      });
    },
    eligibilityInitialSelect() {
      const eligibilityOption = this.refData.eligibility.options.filter((option) => {
        return this.step1.eligibility === option.returnValue;
      });

      return eligibilityOption[0]?.display;
    },
    stateList() {
      return this.refData.state.options.map((state) => {
        return { name: state.display, value: state.returnValue };
      });
    },
    recentDischargeShow() {
      if (EligibilityTypes.EX_SERVING.includes(this.step1.eligibility)) {
        return true;
      }
      return false;
    },
  },

  methods: {
    getStateEligibilityDetails() {
      const step1 = { ...cloneDeep(this.step1) };
      // Get State details
      const policyHolder = findPolicyHolder(this.memberDetails);
      if (policyHolder) {
        const stateCode = policyHolder.residentialAddress.state ?? policyHolder.address.state;
        step1.state = stateCode;
        this.updateCurrentState(stateCode);
      }

      // Get Eligibility details
      if (this.eligibilityDetails) {
        const currentEligibility = mapEligibilityNames(this.eligibilityDetails.eligibilityType);
        if (currentEligibility) {
          step1.eligibility = currentEligibility;
          this.updateCurrentEligibility(currentEligibility);
        }

        // Pre-fill if discharge date is less than 1 year ago
        if (this.eligibilityDetails.dateOfDischarge) {
          const dischargeDate = reverseFormatFormDate(this.eligibilityDetails.dateOfDischarge);
          const oneYearAgoDate = new Date(getPastOrLaterDateFromInitialDate(getCurrentDate(), -1));
          const currentDate = new Date(getCurrentDate());
          step1.recentDischarge = isDateInRange(dischargeDate, oneYearAgoDate, currentDate);
        }
      }
      this.step1 = step1;
    },

    reverseFormatFormDate,
  },

  validations() {
    return {
      step1: {
        state: {
          required,
        },
        eligibility: {
          required,
        },
        recentDischarge: {
          required: requiredIf(function () {
            return this.recentDischargeShow;
          }),
        },
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.dva-card {
  :deep(.slotted-items) {
    margin-bottom: 10px;

    @include mobile {
      grid-column: 1;
      grid-row: 3;
    }
  }

  :deep(.helper-text) {
    grid-row: 1;
  }
}
</style>
